import { containerOf, filedItem, filingRpcArgs, latestClassification } from '@cerebro-diem/core';
import type { Capture, CaptureSource, Classification, ContainerKind, ContainerRef, FilingRequest, ItemKind } from '@cerebro-diem/core';
import { supabase, getSession } from './supabase';
import { isRecord } from './errors';

/** A capture row with its embedded classifications, as returned by `select('*, classifications(*)')`. */
export type CaptureWithClassifications = Capture & { classifications: Classification[] | null };

/** The latest captures, newest first, and where each task or note they became lives now, by its id. */
export async function recentCaptures(
  limit: number,
): Promise<{ captures: CaptureWithClassifications[]; places: Record<string, ContainerRef> }> {
  const { data, error } = await supabase
    .from('captures')
    .select('*, classifications(*)')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  const captures = (data ?? []) as CaptureWithClassifications[];
  const ids: Record<ItemKind, string[]> = { task: [], note: [] };
  for (const capture of captures) {
    const item = filedItem(latestClassification(capture));
    if (item) ids[item.kind].push(item.id);
  }
  const places: Record<string, ContainerRef> = {};
  const load = async (table: 'tasks' | 'notes', columns: string, list: string[]) => {
    if (list.length === 0) return;
    const { data: rows, error: rowsError } = await supabase.from(table).select(columns).in('id', list);
    if (rowsError) throw rowsError;
    for (const row of (rows ?? []) as unknown as { id: string; project_id: string | null; area_id: string | null; resource_id?: string | null }[]) {
      const place = containerOf(row);
      if (place) places[row.id] = place;
    }
  };
  await Promise.all([load('tasks', 'id, project_id, area_id', ids.task), load('notes', 'id, project_id, area_id, resource_id', ids.note)]);
  return { captures, places };
}

/** At most this many waiting captures are listed at once; the rest show as they are dealt with. */
export const INBOX_LIMIT = 200;

/**
 * What the Inbox may hold: captures not filed yet (or whose filing failed)
 * and captures sent to review, asked for directly, newest first, so an old
 * one waiting is never missed behind newer filed ones. The same reads as the
 * phone's Inbox (getInboxCaptures); captureFilingState says which belong.
 */
export async function inboxCaptures(): Promise<{ captures: CaptureWithClassifications[]; hasMore: boolean }> {
  const [unfinished, awaitingReview] = await Promise.all([
    supabase
      .from('captures')
      .select('*, classifications(*)')
      .or('processed.eq.false,processing_error.not.is.null')
      .order('created_at', { ascending: false })
      .limit(INBOX_LIMIT),
    supabase.from('classifications').select('capture_id').eq('status', 'needs_review').order('created_at', { ascending: false }).limit(INBOX_LIMIT),
  ]);
  if (unfinished.error) throw unfinished.error;
  if (awaitingReview.error) throw awaitingReview.error;
  const captures = (unfinished.data ?? []) as CaptureWithClassifications[];
  const seen = new Set(captures.map((capture) => capture.id));
  const reviewIds = [...new Set(((awaitingReview.data ?? []) as { capture_id: string }[]).map((row) => row.capture_id))].filter((id) => !seen.has(id));
  if (reviewIds.length > 0) {
    const reviewed = await supabase.from('captures').select('*, classifications(*)').in('id', reviewIds);
    if (reviewed.error) throw reviewed.error;
    captures.push(...((reviewed.data ?? []) as CaptureWithClassifications[]));
  }
  captures.sort((a, b) => b.created_at.localeCompare(a.created_at));
  return { captures, hasMore: (unfinished.data ?? []).length === INBOX_LIMIT || (awaitingReview.data ?? []).length === INBOX_LIMIT };
}

/**
 * How many of the signed-in user's captures are not filed yet: neither filed
 * nor sent to review, so the server's sweep is still filing them or has
 * stopped retrying them. The server's exact count, never a count of a capped list.
 */
export async function unfiledCaptureCount(): Promise<number> {
  const { count, error } = await supabase.from('captures').select('id', { count: 'exact', head: true }).not('processed', 'is', true);
  if (error) throw error;
  if (count === null) throw new Error('the server did not say how many captures are not filed yet');
  return count;
}

/**
 * Save a capture as the signed-in user under the id this browser chose for
 * it, and return that id. Throws unless the row exists. Sending the same id
 * again is how a lost answer is retried: the server refuses the repeat as a
 * duplicate (23505), and when the row with that id is the user's, the capture
 * is saved and nothing is written twice.
 */
export async function insertCapture(rawText: string, source: CaptureSource, id: string, ownerId: string, createdAt: string): Promise<string> {
  const {
    data: { session },
  } = await getSession();
  if (!session || session.user.id !== ownerId) throw new Error('the signed-in account changed; sign in as the capture owner');

  const receiptColumns = 'id,user_id,raw_text,source,created_at';
  const matches = (row: unknown) => isRecord(row) && row.id === id && row.user_id === ownerId &&
    row.raw_text === rawText && row.source === source && typeof row.created_at === 'string' &&
    Date.parse(row.created_at) === Date.parse(createdAt);
  const { data, error } = await supabase
    .from('captures')
    .insert({ id, user_id: ownerId, raw_text: rawText, source, processed: false, created_at: createdAt })
    .select(receiptColumns)
    .single()
    .setHeader('Authorization', `Bearer ${session.access_token}`);
  if (error) {
    if (error.code !== '23505') throw error;
    const existing = await supabase.from('captures').select(receiptColumns).eq('id', id).maybeSingle().setHeader('Authorization', `Bearer ${session.access_token}`);
    if (existing.error) throw existing.error;
    if (!matches(existing.data)) throw new Error('the server did not confirm the original capture');
    return id;
  }
  if (!matches(data)) throw new Error('the server did not confirm the original capture');
  return id;
}

/** Where a filing put the capture. */
export interface Filed {
  destinationId: string;
  kind: ItemKind;
  containerKind: ContainerKind;
  containerId: string;
}

/** File (or re-file) a capture through the `file_capture_into` database function. Throws unless the server says where it went. */
export async function fileCaptureInto(captureId: string, request: FilingRequest): Promise<Filed> {
  const { data, error } = await supabase.rpc('file_capture_into', filingRpcArgs(captureId, request));
  if (error) throw error;
  if (!isRecord(data) || typeof data.destination_id !== 'string' || typeof data.container_id !== 'string') {
    throw new Error('filing did not say where the capture went');
  }
  return { destinationId: data.destination_id, kind: request.kind, containerKind: request.container.kind, containerId: data.container_id };
}

/** Delete one capture. Throws when nothing was deleted, so a silent no-op is never shown as done. */
export async function deleteCapture(captureId: string): Promise<void> {
  const { data, error } = await supabase.from('captures').delete().eq('id', captureId).select('id');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('capture not found; it may already be deleted');
}
